import { Injectable, inject, signal } from '@angular/core';
import { ChatLoggingService } from '../services/chat-logging.service';

export type ThemeMode = 'light' | 'dark';

/**
 * Service to manage theme switching using PrimeNG's theme system
 * Uses CSS class-based theme switching with the Atomic preset
 */
@Injectable({
  providedIn: 'root'
})
export class PrimeThemeService {
  // Signal to track current theme mode
  public readonly currentTheme = signal<ThemeMode>('light');

  private chatLoggingService = inject(ChatLoggingService);

  /**
   * Set the theme mode and update document class
   * Applies the library-specific .cl-chat-dark class used for the chat
   * component's dark-mode CSS/Tailwind styling.
   */
  setTheme(mode: ThemeMode): void {
    this.currentTheme.set(mode);
    this.chatLoggingService.info({ logMessage: '[PrimeThemeService] theme set to:', logParameters: [mode] });

    // Update document class for CSS-based theme switching
    const rootElement = document.documentElement;
    if (mode === 'dark') {
      rootElement.classList.add('cl-chat-dark');
    } else {
      rootElement.classList.remove('cl-chat-dark');
    }
  }

  /**
   * Toggle between light and dark themes
   */
  toggleTheme(): void {
    const newTheme = this.currentTheme() === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }

  /**
   * Get current theme mode
   */
  getTheme(): ThemeMode {
    return this.currentTheme();
  }

  /**
   * Check if current theme is dark
   */
  isDarkMode(): boolean {
    return this.currentTheme() === 'dark';
  }
}
